import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getEventRegistrations,
  getEventAttendeeStats,
  exportAttendeesToCSV,
  exportAttendeesToExcel,
  updateRegistrationStatus,
  deleteRegistration
} from '../../services/attendeeService';

const AttendeeList = ({ event }) => {
  const { hasRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'company', 'registrationDate'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc', 'desc'
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState('csv');
  const [includePaymentInfo, setIncludePaymentInfo] = useState(false);

  const isAdmin = hasRole('admin');

  useEffect(() => {
    loadRegistrations();
  }, [event.id]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const [registrationsData, statsData] = await Promise.all([
        getEventRegistrations(event.id),
        getEventAttendeeStats(event.id)
      ]);
      setRegistrations(registrationsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-apn-blue mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading attendees...</p>
      </div>
    );
  }

  // Use registrations data or fallback to event.attendees
  const attendeesToShow = registrations.length > 0 ? registrations : (event.attendees || []);

  if (attendeesToShow.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-gray-500">No attendees registered for this event yet.</p>
      </div>
    );
  }

  // Filter attendees based on search term
  const filteredAttendees = attendeesToShow.filter(attendee => {
    const fullName = `${attendee.firstName || ''} ${attendee.lastName || ''}`.toLowerCase();
    const company = (attendee.company || '').toLowerCase();
    const title = (attendee.title || '').toLowerCase();
    const email = (attendee.email || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    return (
      fullName.includes(searchLower) ||
      company.includes(searchLower) ||
      title.includes(searchLower) ||
      email.includes(searchLower)
    );
  });

  // Sort attendees
  const sortedAttendees = [...filteredAttendees].sort((a, b) => {
    let valueA, valueB;

    if (sortBy === 'name') {
      valueA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
      valueB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
    } else if (sortBy === 'company') {
      valueA = (a.company || '').toLowerCase();
      valueB = (b.company || '').toLowerCase();
    } else if (sortBy === 'registrationDate') {
      valueA = a.createdAt ? new Date(a.createdAt.seconds * 1000) : new Date(a.registrationDate || 0);
      valueB = b.createdAt ? new Date(b.createdAt.seconds * 1000) : new Date(b.registrationDate || 0);
    }

    if (sortDirection === 'asc') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Handle CSV export (Admin only)
  const handleExportCSV = async () => {
    if (!isAdmin) {
      alert('Only administrators can export attendee lists.');
      return;
    }

    try {
      const csvContent = await exportAttendeesToCSV(event.id, includePaymentInfo);

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Set up download link
      link.href = url;
      link.setAttribute('download', `${event.title.replace(/\s+/g, '_')}_attendees.csv`);
      document.body.appendChild(link);

      // Trigger download and clean up
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting attendees:', error);
      alert('Failed to export attendee list. Please try again.');
    }
  };

  // Handle Excel export (Admin only)
  const handleExportExcel = async () => {
    if (!isAdmin) {
      alert('Only administrators can export attendee lists.');
      return;
    }

    try {
      const result = await exportAttendeesToExcel(event.id, includePaymentInfo);

      if (result.success) {
        alert(`Successfully exported ${result.attendeeCount} attendees to ${result.filename}`);
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export attendee list to Excel. Please try again.');
    }
  };

  return (
    <div>
      {/* Search and Export */}
      <div className="flex flex-col md:flex-row justify-between mb-4 space-y-4 md:space-y-0">
        <div className="w-full md:w-64">
          <input
            type="text"
            placeholder="Search attendees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
          />
        </div>
        <div className="flex items-center space-x-4">
          {/* Export Options */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">
              <input
                type="checkbox"
                checked={includePaymentInfo}
                onChange={(e) => setIncludePaymentInfo(e.target.checked)}
                className="mr-1"
                disabled={!isAdmin}
              />
              Include Payment Info {!isAdmin && '(Admin Only)'}
            </label>
          </div>

          <button
            onClick={handleExportCSV}
            className="flex items-center text-apn-blue hover:text-blue-700 transition duration-200"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>

          {isAdmin && (
            <button
              onClick={handleExportExcel}
              className="flex items-center text-green-600 hover:text-green-700 transition duration-200"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Attendee Count */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Showing {filteredAttendees.length} of {event.attendees.length} attendees
        </p>
      </div>

      {/* Attendee Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  <span>Name</span>
                  {sortBy === 'name' && (
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={sortDirection === 'asc' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                      />
                    </svg>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('company')}
              >
                <div className="flex items-center">
                  <span>Company</span>
                  {sortBy === 'company' && (
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={sortDirection === 'asc' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                      />
                    </svg>
                  )}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('registrationDate')}
              >
                <div className="flex items-center">
                  <span>Registered</span>
                  {sortBy === 'registrationDate' && (
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={sortDirection === 'asc' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                      />
                    </svg>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAttendees.map((attendee) => (
              <tr key={attendee.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {/* Placeholder for profile photo */}
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 text-xs">
                          {attendee.firstName.charAt(0)}{attendee.lastName.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {attendee.firstName} {attendee.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{attendee.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{attendee.company}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{attendee.title}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    attendee.isMember
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {attendee.isMember ? 'Member' : 'Non-Member'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(attendee.registrationDate).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {filteredAttendees.length === 0 && (
        <div className="text-center py-6">
          <p className="text-gray-500">No attendees found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default AttendeeList;
